import "./datadog";
import "./globals.css";
import { fromPromise } from "@apollo/client";
import type { FetchResult, Observable } from "@apollo/client/core";
import { ErrorLink, type ErrorResponse } from "@apollo/client/link/error";
import { ClerkProvider, useAuth, useOrganizationList } from "@clerk/react";
import {
  ApolloWrapper,
  RouterContext,
  SpinnerContext,
  Toaster,
} from "@repo/ui";
import { RouterProvider } from "@tanstack/react-router";
import countries from "i18n-iso-countries";
import countriesEn from "i18n-iso-countries/langs/en.json";
import type React from "react";
import { StrictMode, useMemo } from "react";
import ReactDOM from "react-dom/client";
import spinnerUrl from "~/assets/animations/primary-spinner.riv";
import { config } from "./config";
import {
  RouterCtxNavLink,
  routerCtxUseTanstackSearchParams,
} from "./pkgui-router-context";
import { router } from "./router";

countries.registerLocale(countriesEn);

function OpsApolloWrapper({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const { setActive } = useOrganizationList();

  const setActiveOrg = async (): Promise<void> => {
    if (setActive) {
      await setActive({ organization: config.primaryOpsOrgId });
    }
  };

  const setActiveOrganisationAndRetry = ({
    operation,
    forward,
  }: ErrorResponse): Observable<FetchResult> => {
    return fromPromise(setActiveOrg()).flatMap(() => forward(operation));
  };

  const errorLink = new ErrorLink((err) => {
    if (
      err.networkError &&
      "statusCode" in err.networkError &&
      err.networkError.statusCode === 404
    ) {
      if (
        "result" in err.networkError &&
        typeof err.networkError.result === "object" &&
        err.networkError.result.code === "organisation_not_found"
      ) {
        return setActiveOrganisationAndRetry(err);
      } else if (!auth.orgId) {
        return setActiveOrganisationAndRetry(err);
      } else {
        auth.signOut();
      }
    } else if (
      err.networkError &&
      "statusCode" in err.networkError &&
      err.networkError.statusCode === 403
    ) {
      if (
        "result" in err.networkError &&
        typeof err.networkError.result === "object" &&
        err.networkError.result.code === "mfa_claim_required"
      ) {
        router.navigate({ to: "/user-profile#/security" });
      } else {
        auth.signOut();
      }
    }
  });

  return (
    <ApolloWrapper baseUrl={`${config.apiUrl}/graphql`} links={[errorLink]}>
      {children}
    </ApolloWrapper>
  );
}

function RouterWrapper() {
  const pkgUIRouterContext = useMemo(
    () => ({
      NavLink: RouterCtxNavLink,
      useSearchParams: routerCtxUseTanstackSearchParams,
    }),
    [],
  );

  return (
    <SpinnerContext.Provider value={{ spinnerUrl }}>
      <RouterContext.Provider value={pkgUIRouterContext}>
        <RouterProvider router={router} />
      </RouterContext.Provider>
    </SpinnerContext.Provider>
  );
}

const rootElement = document.getElementById("app")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <ClerkProvider
        publishableKey={config.clerkClientKey}
        afterSignOutUrl="/sign-in"
        routerPush={(to) => router.navigate({ to })}
        routerReplace={(to) => router.navigate({ to, replace: true })}
      >
        <OpsApolloWrapper>
          <Toaster />
          <RouterWrapper />
        </OpsApolloWrapper>
      </ClerkProvider>
    </StrictMode>,
  );
}
